import { computed, getCurrentScope, onScopeDispose, ref, watch } from 'vue'
import type { StoreAvailabilityCheckSuccess } from '~/services/api/generated'
import type { StoreAvailabilityDataNormalized } from '~/services/api/stores/StoreAvailabilityDataNormalized'
import type { PublicStore } from '~/types/PublicStore'
import type { AvailabilityCheckAttempt } from './AvailabilityCheckAttempt'
import type { StoreAvailabilityCheckOutcome } from './StoreAvailabilityCheckOutcome'
import type { StoreAvailabilityState } from './StoreAvailabilityState'
import { failedAvailabilityOutcome } from './failedAvailabilityOutcome'
import { performAvailabilityCheckAttempt } from './performAvailabilityCheckAttempt'
import { performLoadStoreContext } from './performLoadStoreContext'
import { resolveSelectedRequestDateForApi } from './resolveSelectedRequestDateForApi'
import { useStoreClosure } from './useStoreClosure'
import { useLocalizedMessage } from './useLocalizedMessage'
import type { StoreOrderingContextInputs } from './StoreOrderingContextInputs'
import type { TranslateFn } from './TranslateFn'

const MAX_AVAILABILITY_CHECK_ATTEMPTS = 3

const normalizeAvailabilityReason = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

const formatStoreAvailabilityReason = (value: unknown) => {
  const raw = String(value ?? '').trim()
  const normalized = normalizeAvailabilityReason(raw)
  if (!raw) return 'Store closed at selected time'
  if (
    normalized.includes('outside_operating_hours') ||
    normalized.includes('store_closed') ||
    normalized.includes('closed')
  ) {
    return 'Store closed at selected time'
  }
  if (normalized.includes('order_type')) return 'Not available for selected order type'
  return raw
}

// Closure blocks ordering via `isFullPageBlockVisible` (folds in both the store's own
// fields and a server-detected closure), not a separate branch here.
function resolveCanOrder(
  hasStore: boolean,
  isRefreshing: boolean,
  isFullPageBlockVisible: boolean,
  storeSupportsOrderType: boolean,
  result: StoreAvailabilityCheckSuccess | null
): boolean {
  if (!hasStore) return false
  if (isRefreshing) return false
  if (isFullPageBlockVisible) return false
  if (!storeSupportsOrderType) return false
  if (result === null) return true
  return result.data.available && result.success
}

// A temporary closure arrives only as a thrown problem, never inside a success body, so a
// parsed result here is never itself a closure.
function resolveAvailabilityDisplayReason(result: StoreAvailabilityCheckSuccess | null): string {
  if (!result || (result.data.available && result.success)) return ''
  const flatData: StoreAvailabilityDataNormalized = result.data
  const reason = flatData.reason ?? result.message ?? 'Store closed at selected time'
  return formatStoreAvailabilityReason(reason)
}

type AvailabilityApplyEffect =
  | { kind: 'clear' }
  | { kind: 'error'; message: () => string; data: StoreAvailabilityDataNormalized }

function resolveAvailabilityApplyEffect(
  data: StoreAvailabilityCheckSuccess,
  reasonText: string,
  t: TranslateFn
): AvailabilityApplyEffect {
  if (data.data.available && data.success) return { kind: 'clear' }
  const message = () => reasonText || t('commerce.availabilityErrorModal.notAvailableForRequest')
  return { data: data.data, kind: 'error', message }
}

function resolveAvailabilityPrecondition(
  hasStoreSlug: boolean,
  orderDetailsMissingReason: string,
  storeSupportsOrderType: boolean
): 'store-not-loaded' | 'order-details-missing' | 'order-type-unsupported' | null {
  if (!hasStoreSlug) return 'store-not-loaded'
  if (orderDetailsMissingReason !== '') return 'order-details-missing'
  if (!storeSupportsOrderType) return 'order-type-unsupported'
  return null
}

function canRefreshAvailabilityAfterStoreLoad(
  inputs: Pick<StoreOrderingContextInputs, 'orderDetailsMissingReason' | 'slug'>,
  store: { readonly value: PublicStore | null }
): boolean {
  return inputs.orderDetailsMissingReason.value === '' && store.value?.slug === inputs.slug.value
}

// A slug-only navigation changes `storeContextKey` (and `availabilityContextKey`) immediately,
// before the new store commits; the availability watcher below fires at that moment and bails
// against the still-stale prior store, then never refires on its own since
// `availabilityContextKey` itself doesn't change again once the store catches up. This runs
// once the matching store has actually committed, so a navigation never leaves the new store's
// availability unchecked.
function refreshAvailabilityAfterStoreLoad(
  inputs: Pick<StoreOrderingContextInputs, 'orderDetailsMissingReason' | 'slug'>,
  store: { readonly value: PublicStore | null },
  refreshAvailability: (options: { checkMenu?: boolean }) => Promise<StoreAvailabilityCheckOutcome>
): void {
  if (!canRefreshAvailabilityAfterStoreLoad(inputs, store)) return
  void refreshAvailability({ checkMenu: true })
}

function applyAvailabilityContextKeyChange(
  inputs: Pick<StoreOrderingContextInputs, 'orderDetailsMissingReason' | 'slug'>,
  store: { readonly value: PublicStore | null },
  resetAvailability: () => void,
  refreshAvailability: (options: { checkMenu?: boolean }) => Promise<StoreAvailabilityCheckOutcome>
): void {
  if (inputs.orderDetailsMissingReason.value !== '') {
    resetAvailability()
    return
  }
  if (store.value?.slug !== inputs.slug.value) return
  void refreshAvailability({ checkMenu: true })
}

interface RefreshAvailabilityDeps {
  store: { readonly value: PublicStore | null }
  // The currently-selected slug, distinct from `store.value?.slug`: a still-loading
  // navigation to a different store leaves the prior store committed here for a beat, and a
  // retry must not treat that stale match as this context's own store being ready.
  expectedSlug: { readonly value: string }
  orderDetailsMissingReason: { readonly value: string }
  storeSupportsOrderType: () => boolean
  availabilityContextKey: { readonly value: string }
  attemptAvailabilityCheck: (options: { checkMenu?: boolean }) => Promise<AvailabilityCheckAttempt>
  // Joins the single-flight refresh for whichever context key is current, carrying forward
  // attempts already spent, instead of issuing a duplicate fetch for that key.
  joinCurrentRefresh: (
    options: { checkMenu?: boolean },
    attemptsUsed: number
  ) => Promise<StoreAvailabilityCheckOutcome>
  t: TranslateFn
}

/**
 * Retries a superseded check by joining whichever refresh is current, bounded to
 * `MAX_AVAILABILITY_CHECK_ATTEMPTS` real fetches total.
 */
async function performRefreshAvailability(
  options: { checkMenu?: boolean },
  deps: RefreshAvailabilityDeps,
  attemptsUsed = 0
): Promise<StoreAvailabilityCheckOutcome> {
  const precondition = resolveAvailabilityPrecondition(
    deps.expectedSlug.value !== '' && deps.store.value?.slug === deps.expectedSlug.value,
    deps.orderDetailsMissingReason.value,
    deps.storeSupportsOrderType()
  )
  if (precondition !== null) return { reason: precondition, status: 'skipped' }

  if (attemptsUsed >= MAX_AVAILABILITY_CHECK_ATTEMPTS) {
    return failedAvailabilityOutcome(() => deps.t('commerce.availabilityErrorModal.verifyFailed'))
  }

  const attemptResult = await deps.attemptAvailabilityCheck(options)
  if (attemptResult.settled) return attemptResult.outcome
  // The context moved on mid-fetch -- join whichever refresh is (or becomes) current for the new
  // key instead of issuing a second, redundant fetch for it here.
  return deps.joinCurrentRefresh(options, attemptsUsed + 1)
}

/**
 * Single owner of the store page's ordering-context state (store, availability, closure,
 * `canOrder`) shared by every consumer instead of each re-deriving its own.
 */
export function useStoreOrderingContext(inputs: StoreOrderingContextInputs) {
  const active = ref(true)
  const store = ref<PublicStore | null>(null)
  const timezone = computed(() => store.value?.timezone ?? 'UTC')
  // Resolved here, not accepted as an input: the store resource that owns this timezone is
  // itself owned by this composable, so only it can convert the raw selection.
  const eventDate = computed(() =>
    resolveSelectedRequestDateForApi(
      inputs.rawEventDate.value,
      inputs.eventTime.value,
      timezone.value
    )
  )
  const loading = ref(true)
  const refreshing = ref(false)
  const hasLoadedOnce = ref(false)
  const error = useLocalizedMessage()
  const availabilityState = ref<StoreAvailabilityState>({ status: 'unchecked' })
  // Counts availability-check attempts currently in flight across every context key, so a
  // superseded attempt settling never clears `checking` while a newer attempt is still running.
  const checkingCount = ref(0)
  const checking = computed(() => checkingCount.value > 0)

  if (getCurrentScope() !== undefined) {
    onScopeDispose(() => {
      active.value = false
      checkingCount.value = 0
    })
  }

  const showAvailabilityErrorModal = ref(false)
  const availabilityError = useLocalizedMessage()
  const availabilityErrorData = ref<StoreAvailabilityDataNormalized | null>(null)

  // Identifies the check's "moment" so a superseded response is never committed; includes
  // slug/coordinates/searchQuery even though they don't affect the store-details fetch below.
  const availabilityContextKey = computed(() =>
    JSON.stringify({
      active: active.value,
      coordinates: inputs.coordinates.value,
      eventDate: eventDate.value,
      eventTime: inputs.eventTime.value,
      guestCount: inputs.guestCount.value,
      orderType: inputs.orderType.value,
      searchQuery: inputs.searchQuery.value,
      userId: inputs.userId.value,
      slug: inputs.slug.value
    })
  )

  // A state left over from a superseded key reads as `unchecked` instead of leaking stale
  // data into the current context's projections.
  const currentAvailabilityState = computed<StoreAvailabilityState>(() => {
    const state = availabilityState.value
    return state.status !== 'unchecked' && state.key === availabilityContextKey.value
      ? state
      : { status: 'unchecked' }
  })

  const currentClosedState = computed(() =>
    currentAvailabilityState.value.status === 'closed' ? currentAvailabilityState.value : null
  )

  const availabilityResult = computed(() =>
    currentAvailabilityState.value.status === 'checked'
      ? currentAvailabilityState.value.result
      : null
  )

  const {
    isTemporarilyClosed,
    activeClosure,
    isFullPageBlockVisible,
    message: closureMessage
  } = useStoreClosure(store, inputs.t, inputs.defaultClosedMessage, currentClosedState)

  const canOrder = computed(() =>
    resolveCanOrder(
      store.value !== null,
      refreshing.value,
      isFullPageBlockVisible.value,
      inputs.storeSupportsOrderType(),
      availabilityResult.value
    )
  )

  const showAvailabilityBlock = (
    message: () => string,
    data: StoreAvailabilityDataNormalized | null = null
  ) => {
    showAvailabilityErrorModal.value = true
    availabilityError.set(message)
    availabilityErrorData.value = data
  }

  const closeAvailabilityBlock = () => {
    showAvailabilityErrorModal.value = false
  }

  // Used when the order context becomes incomplete (no date/time), so a stale result from a
  // previous context is never shown or reused.
  const resetAvailability = () => {
    availabilityState.value = { status: 'unchecked' }
    showAvailabilityErrorModal.value = false
    availabilityError.clear()
    availabilityErrorData.value = null
  }

  const getFirstAvailabilityReason = () =>
    resolveAvailabilityDisplayReason(availabilityResult.value)

  // Dispatches `resolveAvailabilityApplyEffect`'s decision onto the shared refs for `key`.
  const applyAvailabilityResult = (data: StoreAvailabilityCheckSuccess, key: string) => {
    const reasonText = resolveAvailabilityDisplayReason(data)
    const effect = resolveAvailabilityApplyEffect(data, reasonText, inputs.t)
    availabilityState.value = { key, result: data, status: 'checked' }

    switch (effect.kind) {
      case 'clear':
        availabilityError.clear()
        availabilityErrorData.value = null
        return
      case 'error':
        availabilityError.set(effect.message)
        availabilityErrorData.value = effect.data
    }
  }

  // `useStoreClosure` folds this into `isFullPageBlockVisible`/`closureMessage` as the
  // full-page block; this deliberately skips the availability modal.
  const applyClosureFailure = (message: () => string, key: string) => {
    availabilityState.value = {
      key,
      get message() {
        return message()
      },
      status: 'closed'
    }
    showAvailabilityErrorModal.value = false
    availabilityError.set(message)
    availabilityErrorData.value = null
  }

  // Opens the availability modal for any non-closure failure.
  const applyGenericFailure = (message: () => string, key: string) => {
    availabilityState.value = {
      key,
      get message() {
        return message()
      },
      status: 'failed'
    }
    showAvailabilityErrorModal.value = true
    availabilityError.set(message)
    availabilityErrorData.value = null
  }

  // Excludes order type: the store-details endpoint doesn't vary by it, so including it would
  // trigger a spurious refetch on every order-type change.
  const storeContextKey = computed(() =>
    !active.value || inputs.slug.value === ''
      ? null
      : JSON.stringify({
          eventDate: eventDate.value,
          eventTime: inputs.eventTime.value,
          slug: inputs.slug.value,
          userId: inputs.userId.value
        })
  )

  const loadStoreContext = (): Promise<void> => {
    if (!active.value) return Promise.resolve()
    return performLoadStoreContext({
      error,
      eventDate,
      eventTime: inputs.eventTime,
      fetchStore: inputs.fetchStore,
      hasLoadedOnce,
      loading,
      onStoreFetchError: inputs.onStoreFetchError,
      refreshing,
      slug: inputs.slug,
      store,
      storeContextKey,
      t: inputs.t,
      userId: inputs.userId
    })
  }

  // Runs even while `store` is still null from an earlier in-flight/superseded request;
  // gating on `store` would leave a stale-key request permanently stuck.
  watch(storeContextKey, (key) => {
    if (key === null) return
    void loadStoreContext().then(() => {
      if (!active.value) return
      refreshAvailabilityAfterStoreLoad(inputs, store, refreshAvailability)
    })
  })

  const attemptAvailabilityCheck = (options: { checkMenu?: boolean }) =>
    performAvailabilityCheckAttempt(options, {
      applyAvailabilityResult,
      applyClosureFailure,
      applyGenericFailure,
      availabilityContextKey,
      checking: {
        start: () => {
          checkingCount.value += 1
        },
        stop: () => {
          if (!active.value) return
          checkingCount.value -= 1
        }
      },
      eventDate,
      eventTime: inputs.eventTime,
      expectedSlug: inputs.slug,
      fetchAvailability: inputs.fetchAvailability,
      getFirstAvailabilityReason,
      onAvailabilityFetchError: inputs.onAvailabilityFetchError,
      onTemporaryClosureDetected: inputs.onTemporaryClosureDetected,
      store,
      t: inputs.t,
      timezone
    })

  // Single-flight map keyed by (availabilityContextKey, checkMenu); a key already visited
  // (`visitedFlightKeys`) starts fresh instead of joining its own unresolved ancestor.
  const inFlightRefreshes = new Map<string, Promise<StoreAvailabilityCheckOutcome>>()

  const startRefresh = (
    options: { checkMenu?: boolean },
    attemptsUsed: number,
    visitedFlightKeys: ReadonlySet<string>
  ): Promise<StoreAvailabilityCheckOutcome> => {
    if (!active.value) return Promise.resolve({ reason: 'context-disposed', status: 'skipped' })
    const flightKey = `${availabilityContextKey.value}::${options.checkMenu === true}`
    const inFlight = inFlightRefreshes.get(flightKey)
    if (inFlight !== undefined && !visitedFlightKeys.has(flightKey)) return inFlight

    const nextVisitedFlightKeys = new Set(visitedFlightKeys)
    nextVisitedFlightKeys.add(flightKey)

    const refresh = performRefreshAvailability(
      options,
      {
        attemptAvailabilityCheck,
        availabilityContextKey,
        expectedSlug: inputs.slug,
        joinCurrentRefresh: (joinOptions, joinAttemptsUsed) =>
          startRefresh(joinOptions, joinAttemptsUsed, nextVisitedFlightKeys),
        orderDetailsMissingReason: inputs.orderDetailsMissingReason,
        store,
        storeSupportsOrderType: inputs.storeSupportsOrderType,
        t: inputs.t
      },
      attemptsUsed
    ).finally(() => {
      if (inFlightRefreshes.get(flightKey) === refresh) inFlightRefreshes.delete(flightKey)
    })
    inFlightRefreshes.set(flightKey, refresh)
    return refresh
  }

  const refreshAvailability = (
    options: { checkMenu?: boolean } = {}
  ): Promise<StoreAvailabilityCheckOutcome> => startRefresh(options, 0, new Set())

  // Automatic for every consumer of this composable; no separate wiring needed per caller.
  watch(availabilityContextKey, () => {
    if (!active.value) return
    applyAvailabilityContextKeyChange(inputs, store, resetAvailability, refreshAvailability)
  })

  return {
    activeClosure,
    availabilityErrorData,
    availabilityErrorMessage: availabilityError.message,
    availabilityResult,
    availabilityState,
    canOrder,
    checking,
    closeAvailabilityBlock,
    closureMessage,
    error: error.message,
    eventDate,
    getFirstAvailabilityReason,
    isFullPageBlockVisible,
    isTemporarilyClosed,
    loadStoreContext,
    loading,
    refreshAvailability,
    refreshing,
    resetAvailability,
    showAvailabilityBlock,
    showAvailabilityErrorModal,
    // Read-only: the only writer is `performLoadStoreContext`, kept internal to this composable.
    store: computed(() => store.value),
    timezone
  }
}
